import { msg } from '@lingui/core/macro'
import type { StatusLightToken } from '@oranje/ui'

import { labelMap } from '@/shared/lib/i18nLabels'

/**
 * Semáforo de Requisición: estados, transiciones válidas y cómo se pintan.
 *
 * Códigos copiados del seed de la API (`apps/api/prisma/seed.ts`, semáforo
 * REQUISITION): identificadores en inglés por D-11, etiquetas en español.
 *
 * ⚠ Mismo domicilio provisional que el de Onboarding: su sitio es
 * `packages/domain/src/statusLights/requisitionStatusLight.ts`, hoy fuera del alcance
 * acordado.
 *
 * `APPLE_GREEN` (En elaboración) se queda SOLO como historia: desde el
 * 2026-10-29 la requisición nace Autorizada y la migración firmó las que
 * esperaban. Sigue aquí para nombrar los pasos viejos de la historia.
 */
export const REQUISITION_STATUSES = [
  'APPLE_GREEN',
  'GREEN',
  'YELLOW',
  'LIGHT_BLUE',
  'RED',
  'PURPLE',
] as const

export type RequisitionStatus = (typeof REQUISITION_STATUSES)[number]

/** Qué significa cada color EN ESTE semáforo. En otro dice otra cosa. */
const REQUISITION_STATUS_MESSAGE = {
  APPLE_GREEN: msg`En elaboración`,
  GREEN: msg`Autorizada`,
  YELLOW: msg`En proceso`,
  LIGHT_BLUE: msg`Cubierta totalmente`,
  RED: msg`Cubierta parcialmente`,
  PURPLE: msg`Eliminada`,
}

export const REQUISITION_STATUS_LABEL: Record<RequisitionStatus, string> = labelMap(
  REQUISITION_STATUS_MESSAGE,
)

export const REQUISITION_STATUS_TOKEN: Record<RequisitionStatus, StatusLightToken> = {
  APPLE_GREEN: 'st-verde-manzana',
  GREEN: 'st-verde',
  YELLOW: 'st-amarillo',
  LIGHT_BLUE: 'st-azul-claro',
  RED: 'st-rojo',
  PURPLE: 'st-morado',
}

/** Transcritas del vault. Cambiarlas exige actualizar antes la nota del semáforo. */
export const REQUISITION_TRANSITIONS: Record<RequisitionStatus, readonly RequisitionStatus[]> = {
  APPLE_GREEN: ['GREEN', 'PURPLE'],
  GREEN: ['YELLOW', 'PURPLE'],
  YELLOW: ['LIGHT_BLUE', 'RED'],
  LIGHT_BLUE: [],
  RED: ['YELLOW'],
  PURPLE: [],
}

/**
 * Semáforo de Urgencia: cuánto falta para que empiece la requisición.
 *
 * Lo calcula el BACKEND a partir de la fecha de inicio; el front solo lo pinta.
 * Si lo calculara aquí, dos pantallas abiertas a distinta hora mostrarían
 * urgencias distintas para la misma requisición.
 *
 * ⚠ El «verde» de ESTE semáforo es `STRONG_GREEN` (Verde fuerte) en el seed,
 * no `GREEN`: los códigos no se comparten entre semáforos.
 */
export const URGENCY_LEVELS = ['RED', 'YELLOW', 'STRONG_GREEN'] as const

export type UrgencyLevel = (typeof URGENCY_LEVELS)[number]

/**
 * El nombre de cada estado es el del vault (Urgente · Medio · Normal): un
 * umbral como «< 72 h» no le dice nada a quien mira el tablero (Hugo,
 * 2026-09-21). El umbral vive en `URGENCY_HINT`, en días y en palabras, para
 * el título del chip y las frases que lo necesiten.
 */
const URGENCY_MESSAGE = {
  RED: msg`Urgente`,
  YELLOW: msg`Medio`,
  STRONG_GREEN: msg`Normal`,
}

export const URGENCY_LABEL: Record<UrgencyLevel, string> = labelMap(URGENCY_MESSAGE)

const URGENCY_HINT_MESSAGE = {
  RED: msg`empieza en menos de 3 días`,
  YELLOW: msg`empieza en 3 a 5 días`,
  STRONG_GREEN: msg`empieza en más de 5 días`,
}

/** «empieza en menos de 3 días»: lo que significa el color, para decirlo al lado. */
export const URGENCY_HINT: Record<UrgencyLevel, string> = labelMap(URGENCY_HINT_MESSAGE)

export const URGENCY_TOKEN: Record<UrgencyLevel, StatusLightToken> = {
  RED: 'st-rojo',
  YELLOW: 'st-amarillo',
  STRONG_GREEN: 'st-verde',
}

/**
 * El color por su nombre, para cuando hay que nombrarlo en una frase: «las 2
 * posiciones nacen en Rojo». Ahí el chip no sirve porque va dentro del texto.
 */
export const URGENCY_COLOR_NAME: Record<UrgencyLevel, string> = {
  RED: 'Rojo',
  YELLOW: 'Amarillo',
  STRONG_GREEN: 'Verde',
}
